import { useState } from 'react'

import { useOpenWindow } from '@krp/client-runtime'
import { ACTIVE_POLL_MS, SYNC_WINDOWS, SyncHomeScreen, shownError, syncCheck, syncSummary, type ShownError, type SyncHomeActions } from '@krp/sync-ui'

import { CONNECTION_FORMS } from '../../composition/connectionForms'
import { queryState, useOperationIds } from './syncKit'

/**
 * Вход раздела «Синхронизация» (RP-SYN8): сводка источников и общего каталога. Пока где-то идёт проверка, сводка перечитывается
 * раз в 2 с — ход проверки виден на плитке без F5. Окна раздела открываются поверх экрана (`?w=`).
 */
export function SyncHomeBinding() {
  const openWindow = useOpenWindow()
  const [pollMs, setPollMs] = useState(0)
  const summary = syncSummary.useQuery(undefined, { pollingInterval: pollMs })
  const sources = summary.data?.sources ?? []
  const checking = sources.some((source) => source.lastCheck?.state === 'running')
  const wantedPoll = checking ? ACTIVE_POLL_MS : 0
  if (wantedPoll !== pollMs) setPollMs(wantedPoll)

  const [check] = syncCheck.useMutation()
  const { operationFor, settled } = useOperationIds()
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set())
  const [errors, setErrors] = useState<ReadonlyMap<string, ShownError>>(() => new Map())

  const startCheck = (resourceId: string) => {
    const source = sources.find((item) => item.resourceId === resourceId)
    // Одно намерение — «проверить после последней известной проверки»: повтор после обрыва ответа не запускает вторую.
    const intent = `check:${resourceId}:${source?.lastCheck?.id ?? 'none'}`
    setBusy((current) => new Set(current).add(resourceId))
    setErrors((current) => {
      const next = new Map(current)
      next.delete(resourceId)
      return next
    })
    void check({ resourceId, operationId: operationFor(intent) })
      .unwrap()
      .then(() => settled(intent))
      .catch((error: unknown) => setErrors((current) => new Map(current).set(resourceId, shownError(`Не удалось запустить проверку «${source?.displayName ?? resourceId}»`, error))))
      .finally(() =>
        setBusy((current) => {
          const next = new Set(current)
          next.delete(resourceId)
          return next
        }),
      )
  }

  const actions: SyncHomeActions = {
    openDivergences: (source) => openWindow(SYNC_WINDOWS.divergences, { source }),
    check: startCheck,
    openInit: (resourceId) => openWindow(SYNC_WINDOWS.init, { source: resourceId }),
    openIgnores: () => openWindow(SYNC_WINDOWS.ignores),
    openPriceMaps: () => openWindow(SYNC_WINDOWS.priceMaps),
    openSchedule: () => openWindow(SYNC_WINDOWS.schedule),
    openJournal: () => openWindow(SYNC_WINDOWS.journal),
    reload: () => void summary.refetch(),
  }

  const state = queryState(summary, 'Не удалось получить сводку источников')
  return (
    <SyncHomeScreen
      vm={{
        ...state,
        // Источники — в общем порядке (решение владельца 03.11.2026): по виду, внутри вида по названию.
        tiles: [...sources]
          .sort((left, right) =>
            CONNECTION_FORMS.compareSources(
              { kind: left.kind, displayName: left.displayName, id: left.resourceId },
              { kind: right.kind, displayName: right.displayName, id: right.resourceId },
            ),
          )
          .map((source) => ({
            source,
            mark: CONNECTION_FORMS.shortTitleOf(source.kind),
            noun: CONNECTION_FORMS.sourceNounOf(source.kind),
            tone: CONNECTION_FORMS.toneOf(source.kind),
            checkBusy: busy.has(source.resourceId),
            checkError: errors.get(source.resourceId) ?? null,
          })),
        catalog: summary.data?.commonCatalog ?? null,
      }}
      actions={actions}
    />
  )
}
